import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faPlus,
  faTrash,
  faLink,
  faCalendarDay,
  faCalendarWeek,
} from '@fortawesome/free-solid-svg-icons';

const EventDataGenerator = () => {
  const [eventData, setEventData] = useState({
    title: '',
    date: '',
    description: '',
    image: '',
    links: [],
  });

  const [isDateRange, setIsDateRange] = useState(false);
  const [jsonOutput, setJsonOutput] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [newLink, setNewLink] = useState({
    title: '',
    url: '',
  });

  useEffect(() => {
    // Format the date as ISO string if it exists
    const formattedData = { ...eventData };

    if (isDateRange) {
      if (eventData.startDate && eventData.startTime) {
        const startDateObj = new Date(
          `${eventData.startDate}T${eventData.startTime}`
        );
        formattedData.startDate = startDateObj.toISOString();
      }

      if (eventData.endDate && eventData.endTime) {
        const endDateObj = new Date(
          `${eventData.endDate}T${eventData.endTime}`
        );
        formattedData.endDate = endDateObj.toISOString();
      }

      // Remove single date fields if they exist
      delete formattedData.date;
      delete formattedData.time;
    } else {
      if (eventData.date && eventData.time) {
        const dateObj = new Date(`${eventData.date}T${eventData.time}`);
        formattedData.date = dateObj.toISOString();
      }

      // Remove date range fields if they exist
      delete formattedData.startDate;
      delete formattedData.startTime;
      delete formattedData.endDate;
      delete formattedData.endTime;
    }

    // Clean up temporary time fields
    delete formattedData.time;
    delete formattedData.startTime;
    delete formattedData.endTime;

    // Generate JSON output whenever eventData changes
    setJsonOutput(JSON.stringify(formattedData, null, 2));
  }, [eventData, isDateRange]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value,
    });
  };

  const handleNewLinkChange = (e) => {
    const { name, value } = e.target;
    setNewLink({
      ...newLink,
      [name]: value,
    });
  };

  const toggleDateRange = () => {
    setIsDateRange(!isDateRange);

    // Reset date fields when toggling
    if (!isDateRange) {
      // Switching to date range
      setEventData({
        ...eventData,
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
        date: '',
        time: '',
      });
    } else {
      // Switching to single date
      setEventData({
        ...eventData,
        date: '',
        time: '',
        startDate: '',
        startTime: '',
        endDate: '',
        endTime: '',
      });
    }
  };

  const addLink = () => {
    if (newLink.title && newLink.url) {
      setEventData({
        ...eventData,
        links: [...eventData.links, { ...newLink }],
      });
      setNewLink({ title: '', url: '' });
    }
  };

  const removeLink = (index) => {
    const updatedLinks = [...eventData.links];
    updatedLinks.splice(index, 1);
    setEventData({
      ...eventData,
      links: updatedLinks,
    });
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonOutput);
    setCopySuccess('Copied!');
    setTimeout(() => setCopySuccess(''), 2000);
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      <h1 className='text-3xl font-bold mb-6 text-primary-DEFAULT'>
        Event Data Generator
      </h1>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
        <div className='bg-white dark:bg-dark-light p-6 rounded-lg shadow-md'>
          <h2 className='text-xl font-semibold mb-4'>Event Information</h2>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>
              Event Title
            </label>
            <input
              type='text'
              name='title'
              value={eventData.title}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='Event Title'
            />
          </div>

          <div className='mb-4'>
            <div className='flex items-center justify-between mb-2'>
              <label className='block text-sm font-medium'>
                Date Information
              </label>
              <div className='flex items-center'>
                <button
                  onClick={toggleDateRange}
                  className={`px-3 py-1 text-sm rounded-md mr-2 ${
                    !isDateRange
                      ? 'bg-primary-DEFAULT'
                      : 'bg-gray-200 dark:bg-dark-DEFAULT'
                  }`}
                >
                  <FontAwesomeIcon icon={faCalendarDay} className='mr-1' />
                  Single Date
                </button>
                <button
                  onClick={toggleDateRange}
                  className={`px-3 py-1 text-sm rounded-md ${
                    isDateRange
                      ? 'bg-primary-DEFAULT'
                      : 'bg-gray-200 dark:bg-dark-DEFAULT'
                  }`}
                >
                  <FontAwesomeIcon icon={faCalendarWeek} className='mr-1' />
                  Date Range
                </button>
              </div>
            </div>

            {isDateRange ? (
              <div className='space-y-3'>
                <div>
                  <p className='text-sm font-medium mb-1'>Start Date & Time</p>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                    <input
                      type='date'
                      name='startDate'
                      value={eventData.startDate || ''}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
                    />
                    <input
                      type='time'
                      name='startTime'
                      value={eventData.startTime || ''}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
                    />
                  </div>
                </div>
                <div>
                  <p className='text-sm font-medium mb-1'>End Date & Time</p>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                    <input
                      type='date'
                      name='endDate'
                      value={eventData.endDate || ''}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
                    />
                    <input
                      type='time'
                      name='endTime'
                      value={eventData.endTime || ''}
                      onChange={handleInputChange}
                      className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium mb-1'>Date</label>
                  <input
                    type='date'
                    name='date'
                    value={eventData.date || ''}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium mb-1'>Time</label>
                  <input
                    type='time'
                    name='time'
                    value={eventData.time || ''}
                    onChange={handleInputChange}
                    className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
                  />
                </div>
              </div>
            )}
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>
              Description
            </label>
            <textarea
              name='description'
              value={eventData.description}
              onChange={handleInputChange}
              rows='3'
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='Event description...'
            ></textarea>
          </div>

          <div className='mb-4'>
            <label className='block text-sm font-medium mb-1'>
              Image Filename
            </label>
            <input
              type='text'
              name='image'
              value={eventData.image}
              onChange={handleInputChange}
              className='w-full px-3 py-2 border rounded-md dark:bg-dark-DEFAULT dark:border-gray-700'
              placeholder='example.jpg'
            />
            <p className='text-xs text-gray-500 mt-1'>
              Image should be placed in the public/data/events/images/ directory
            </p>
          </div>

          <h3 className='text-lg font-semibold mt-6 mb-3'>Event Links</h3>
          {eventData.links.length > 0 && (
            <div className='mb-4 space-y-2'>
              {eventData.links.map((link, index) => (
                <div
                  key={index}
                  className='flex items-center p-2 border rounded-md dark:border-gray-700'
                >
                  <div className='flex-grow'>
                    <div className='flex items-center'>
                      <FontAwesomeIcon
                        icon={faLink}
                        className='mr-2 text-primary-DEFAULT'
                      />
                      <span className='font-medium'>{link.title}: </span>
                      <span className='ml-2 text-sm truncate'>{link.url}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeLink(index)}
                    className='ml-2 text-red-500 hover:text-red-700'
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className='bg-gray-50 dark:bg-dark-DEFAULT p-4 rounded-md'>
            <h4 className='text-sm font-medium mb-2'>Add New Link</h4>
            <div className='grid grid-cols-1 gap-2 mb-2'>
              <input
                type='text'
                name='title'
                value={newLink.title}
                onChange={handleNewLinkChange}
                className='w-full px-3 py-2 border rounded-md dark:bg-dark-light dark:border-gray-700'
                placeholder='Link Title (e.g., Watch Stream)'
              />
              <input
                type='text'
                name='url'
                value={newLink.url}
                onChange={handleNewLinkChange}
                className='w-full px-3 py-2 border rounded-md dark:bg-dark-light dark:border-gray-700'
                placeholder='URL (e.g., https://twitch.tv/channelname)'
              />
            </div>
            <button
              onClick={addLink}
              className='mt-2 flex items-center px-4 py-2 bg-primary-DEFAULT rounded-md hover:bg-primary-dark'
            >
              <FontAwesomeIcon icon={faPlus} className='mr-1' />
              Add Link
            </button>
          </div>
        </div>

        <div className='bg-white dark:bg-dark-light p-6 rounded-lg shadow-md'>
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-xl font-semibold'>Generated JSON</h2>
            <button
              onClick={copyToClipboard}
              className='flex items-center px-3 py-1 bg-primary-DEFAULT rounded-md hover:bg-primary-dark'
            >
              <FontAwesomeIcon icon={faCopy} className='mr-1' />
              {copySuccess || 'Copy'}
            </button>
          </div>

          <div className='relative'>
            <pre className='p-4 bg-gray-800 text-gray-100 rounded-md overflow-auto max-h-[calc(100vh-300px)]'>
              {jsonOutput || '// Fill out the form to generate JSON'}
            </pre>
          </div>

          <div className='mt-6'>
            <h3 className='text-lg font-semibold mb-2'>Instructions</h3>
            <ol className='list-decimal list-inside space-y-2 text-sm'>
              <li>Fill out all the event information fields</li>
              <li>Select single date or date range based on your event</li>
              <li>Add any links for the event (e.g., stream links)</li>
              <li>Copy the generated JSON to create a new event</li>
              <li>
                Make sure to place your event image in{' '}
                <code className='bg-gray-100 dark:bg-dark-DEFAULT px-1 rounded'>
                  public/data/events/images/
                </code>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDataGenerator;
